import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import type { BillingMode, SubscriptionPlan } from '../types'

interface Props {
  plan: SubscriptionPlan
  selectedMode: BillingMode
  driverCount: number
  recommended?: boolean
  selected?: boolean
  onSelect: () => void
}

const formatPln = (n: number) =>
  `${n.toLocaleString('pl-PL', { maximumFractionDigits: 0 })} zł`

type Translate = ReturnType<typeof useLocalize>['t']

const priceForMode = (
  plan: SubscriptionPlan,
  mode: BillingMode,
  t: Translate,
): { amount: number; periodLabel: string; subLabel: string } => {
  switch (mode) {
    case 'MonthlyCard':
      return {
        amount: plan.monthlyNet,
        periodLabel: t('subscription.plan.perMonth'),
        subLabel: t('subscription.plan.net'),
      }
    case 'Prepaid6m':
      return {
        amount: plan.prepaid6mNet,
        periodLabel: t('subscription.plan.per6m'),
        subLabel: t('subscription.plan.netSave10'),
      }
    case 'Prepaid12m':
      return {
        amount: plan.prepaid12mNet,
        periodLabel: t('subscription.plan.per12m'),
        subLabel: t('subscription.plan.netSave20'),
      }
  }
}

export const PlanCard: React.FC<Props> = ({
  plan,
  selectedMode,
  driverCount,
  recommended,
  selected,
  onSelect,
}) => {
  const { t } = useLocalize()
  const price = priceForMode(plan, selectedMode, t)
  const overage = Math.max(0, driverCount - plan.includedDrivers)

  const border = selected
    ? 'tw-border-primary tw-border-2 tw-shadow-drop'
    : recommended
    ? 'tw-border-primary tw-border'
    : 'tw-border-base-300 tw-border hover:tw-border-content-base/30 hover:tw-shadow-drop'

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`tw-relative tw-text-left tw-p-4 tw-rounded-lg tw-bg-white tw-transition-all tw-cursor-pointer ${border}`}
    >
      {recommended && (
        <span className="tw-absolute -tw-top-2.5 tw-right-3 tw-bg-primary tw-text-white tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider tw-px-2 tw-py-0.5 tw-rounded">
          {t('subscription.plan.recommended')}
        </span>
      )}

      <h3 className="tw-text-lg tw-font-semibold tw-text-content-base tw-m-0">
        {plan.code}
      </h3>
      <p className="tw-text-xs tw-text-content-base/60 tw-mt-1 tw-mb-3">
        {t('subscription.plan.includedPrefix')} {plan.includedDrivers}{' '}
        {plan.includedDrivers === 1
          ? t('subscription.plan.driverOne')
          : t('subscription.plan.driverMany')}{' '}
        {t('subscription.plan.includedSuffix')}
      </p>

      <div className="tw-flex tw-items-baseline tw-gap-1.5">
        <span className="tw-text-2xl tw-font-bold tw-text-content-base">
          {formatPln(price.amount)}
        </span>
        <span className="tw-text-sm tw-text-content-base/60">{price.periodLabel}</span>
      </div>
      <p className="tw-text-[11px] tw-text-content-base/60 tw-mt-0.5">{price.subLabel}</p>

      <p className="tw-text-xs tw-text-content-base/70 tw-mt-3">
        {t('subscription.plan.extraDriver')} <strong>{formatPln(plan.extraDriverNet)}</strong>{' '}
        {t('subscription.plan.perMonth')}
      </p>

      {/* Only surface the overage warning once the user has actually picked
          this card — otherwise every undersized tier shows a scary banner. */}
      {selected && overage > 0 && (
        <p className="tw-text-[11px] tw-text-amber-700 tw-mt-2 tw-bg-amber-50 tw-rounded tw-px-2 tw-py-1.5">
          {t('subscription.plan.overage', {
            count: driverCount,
            over: overage,
            amount: formatPln(plan.extraDriverNet * overage),
          })}
        </p>
      )}
    </button>
  )
}
